import { useEffect, useState, useMemo } from "react";
import { DangerCircle } from "@solar-icons/react/ssr";
import { Autocomplete, Typography, useTheme } from "@mui/material";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import { request } from "@/Api";
import { RadioButton } from "@/common/CommonComponents";
import { CustomBox, TargetsWarning } from "@/common/StyledComponents";
import { SectionTitle } from "@/common/typography";
import { currencies } from "@/constants";
import { setUpdating } from "@/sheet/bidSlice";
import { store } from "@/sheet/bidStore";
import { rateTypes, resourceTypes, INTERNAL_MARKUP } from "@/sheet/constants";

const Subtitle = styled.h3`
    margin: 2px 0 24px 0;
`;

const SmallRed = styled.span`
    color: red;
    font-size: 12px;
`;

function useLinePropagatedValue(lines, lineGroup, field) {
    return useMemo(() => {
        const value = lines.length == 0 ? lineGroup[field] : lines[0][field];
        const hasMismatch = lines.some((line) => line[field] !== value);
        return hasMismatch ? null : value;
    }, [lines, lineGroup, field]);
}

function useGroupPropagatedValue(groups, lineGroup, field) {
    return useMemo(() => {
        const value = groups.length > 0 ? groups[0][field] : lineGroup[field];
        const hasMismatch = groups.some((group) => group[field] !== value);
        return hasMismatch ? null : value;
    }, [groups, lineGroup, field]);
}

export default function LineGroupPopup({
    lineGroup,
    isOpen,
    close,
    popupType,
}) {
    const theme = useTheme();
    const dispatch = useDispatch();

    const isRunning = store.showRunning;

    const [lineGroupLocal, setLineGroupLocal] = useState(lineGroup);
    const [warningOpen, setWarningOpen] = useState(false);
    const [groupUpdating, setGroupUpdating] = useState(false);

    const groups = lineGroup.children;
    const lines = lineGroup.line_items;

    const isOverriding = popupType == "override";
    const isCategory = lineGroup.parent_id ? true : false;
    const lineGroupType = lineGroup.parent_id ? "category" : "section";

    const markupPropogated = useLinePropagatedValue(lines, lineGroup, "markup");
    const markupAmountPropogated = useLinePropagatedValue(
        lines,
        lineGroup,
        "markup_amount",
    );
    const budgetBufferPropogated = useLinePropagatedValue(
        lines,
        lineGroup,
        "budget_buffer",
    );
    const budgetBufferAmountPropogated = useLinePropagatedValue(
        lines,
        lineGroup,
        "budget_buffer_amount",
    );
    const vendorCurrencyPropogated = useLinePropagatedValue(
        lines,
        lineGroup,
        "vendor_currency",
    );

    const rateTypePropogated = useGroupPropagatedValue(
        groups,
        lineGroup,
        "rate_type",
    );
    const resourceTypePropogated = useGroupPropagatedValue(
        groups,
        lineGroup,
        "is_internal",
    );

    useEffect(() => {
        if (Object.keys(lineGroup).length > 0) {
            setLineGroupLocal({
                ...lineGroup,
                markup: lineGroup.is_internal
                    ? INTERNAL_MARKUP
                    : isOverriding
                      ? markupPropogated
                      : lineGroup.markup,
                markup_amount: isOverriding
                    ? markupAmountPropogated
                    : lineGroup.markup_amount,
                budget_buffer: isOverriding
                    ? budgetBufferPropogated
                    : lineGroup.budget_buffer,
                budget_buffer_amount: isOverriding
                    ? budgetBufferAmountPropogated
                    : lineGroup.budget_buffer_amount,
                vendor_currency: isOverriding
                    ? vendorCurrencyPropogated
                    : lineGroup.vendor_currency,
                rate_type: isCategory
                    ? lineGroup.rate_type
                    : isOverriding
                      ? rateTypePropogated
                      : lineGroup.rate_type,
                is_internal: isCategory
                    ? lineGroup.is_internal
                    : isOverriding
                      ? resourceTypePropogated
                      : lineGroup.is_internal,
            });
        }
    }, [lineGroup]);

    const handleInput = (field) => (event) => {
        const isPercentage = ["markup", "budget_buffer"].includes(field);
        const value = event.target.value;
        console.log(value);
        setLineGroupLocal({
            ...lineGroupLocal,
            [field]: isPercentage ? value / 100 : value,
        });
    };

    const handleAutocompleteChange = (field) => (event, newValue) => {
        setLineGroupLocal({ ...lineGroupLocal, [field]: newValue });
    };

    const handleRateTypeChange = (event) => {
        setLineGroupLocal({
            ...lineGroupLocal,
            rate_type: event.target.getAttribute("value"),
        });
    };

    const handleResourceTypeChange = (event) => {
        const isInternal = event.target.getAttribute("value") == "true";
        setLineGroupLocal({
            ...lineGroupLocal,
            is_internal: isInternal,
            markup: isInternal ? INTERNAL_MARKUP : lineGroupLocal.markup,
        });
    };

    const handleCancel = () => {
        close();
    };

    const handleCreate = () => {
        request
            .post(
                `/bids/${lineGroupLocal.bid_id}/line-groups${isRunning ? "/running" : ""}`,
                lineGroupLocal,
            )
            .then((response) => {
                close();
                setWarningOpen(false);
            });
    };

    const updateLineGroup = () => {
        setGroupUpdating(true);
        dispatch(setUpdating(true));
        request
            .put(
                `/bids/${lineGroupLocal.bid_id}/line-groups/${isRunning ? "running/" : ""}${lineGroupLocal.id}`,
                lineGroupLocal,
            )
            .then((response) => {
                close();
                setWarningOpen(false);
                setGroupUpdating(false);
            });
    };

    const popupTypeMapping = {
        create: {
            title: `Create a new ${lineGroupType}`,
            buttonText: "Add",
            buttonCallback: handleCreate,
        },
        override: {
            title: `Override all under ${lineGroupType} (use with caution!)`,
            buttonText: "Override all",
            buttonCallback: () => {
                setWarningOpen(true);
            },
            markupFieldWarning:
                markupPropogated != null ? null : (
                    <SmallRed>(Mixed settings!)</SmallRed>
                ),
            markupAmountFieldWarning:
                markupAmountPropogated != null ? null : (
                    <SmallRed>(Mixed settings!)</SmallRed>
                ),
            budgetBufferFieldWarning:
                budgetBufferPropogated != null ? null : (
                    <SmallRed>(Mixed settings!)</SmallRed>
                ),
            budgetBufferAmountFieldWarning:
                budgetBufferAmountPropogated != null ? null : (
                    <SmallRed>(Mixed settings!)</SmallRed>
                ),
            vendorCurrencyFieldWarning:
                vendorCurrencyPropogated != null ? null : (
                    <SmallRed>(Mixed settings!)</SmallRed>
                ),
            rateTypeFieldWarning:
                isCategory ||
                (!isCategory &&
                    groups.length == 0) ? null : rateTypePropogated !=
                  null ? null : (
                    <SmallRed>(Mixed settings!)</SmallRed>
                ),
            resourceTypeFieldWarning:
                isCategory ||
                (!isCategory &&
                    groups.length == 0) ? null : resourceTypePropogated !=
                  null ? null : (
                    <SmallRed>(Mixed settings!)</SmallRed>
                ),
        },
    };

    return (
        <Modal
            onKeyDown={(e) => {
                e.key != "Enter" ||
                    popupTypeMapping[popupType].buttonCallback();
            }}
            open={isOpen}
            onClose={handleCancel}
        >
            <CustomBox
                sx={{
                    background: theme.palette.background.default,
                    overflow: "auto",
                }}
            >
                <div style={{ marginBottom: 30 }}>
                    <SectionTitle>
                        {popupType ? popupTypeMapping[popupType].title : ""}
                    </SectionTitle>
                    <TextField
                        value={lineGroupLocal.name || ""}
                        onChange={handleInput("name")}
                        size="small"
                        placeholder={`Name ${lineGroupType}`}
                        fullWidth
                    />
                </div>

                <div style={{ marginBottom: 20 }}>
                    <Typography sx={{ paddintTop: 2, paddingBottom: 1 }}>
                        Rate Type{" "}
                        {popupType
                            ? popupTypeMapping[popupType].rateTypeFieldWarning
                            : ""}
                    </Typography>
                    <Grid container spacing={3}>
                        {rateTypes.map((rateTypeOption) => {
                            return (
                                rateTypeOption.name != "Percentage" && (
                                    <Grid key={rateTypeOption.name} md={3} item>
                                        <RadioButton
                                            theme={theme}
                                            value={rateTypeOption.name}
                                            label={rateTypeOption.name}
                                            checked={
                                                lineGroupLocal.rate_type ==
                                                rateTypeOption.name
                                            }
                                            onClick={handleRateTypeChange}
                                        />
                                    </Grid>
                                )
                            );
                        })}
                    </Grid>
                </div>

                <div style={{ marginBottom: 40 }}>
                    <Grid container spacing={3}>
                        <Grid item md={6}>
                            <Typography
                                sx={{ paddintTop: 2, paddingBottom: 1 }}
                            >
                                Vendor Currency{" "}
                                {popupType
                                    ? popupTypeMapping[popupType]
                                          .vendorCurrencyFieldWarning
                                    : ""}
                            </Typography>
                            <Autocomplete
                                options={currencies}
                                value={lineGroupLocal.vendor_currency}
                                onChange={handleAutocompleteChange(
                                    "vendor_currency",
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                    />
                                )}
                            />
                        </Grid>
                        <Grid md={6} item height={"max-content"}>
                            <Typography
                                sx={{ paddintTop: 2, paddingBottom: 1 }}
                            >
                                Resource Type{" "}
                                {popupType
                                    ? popupTypeMapping[popupType]
                                          .resourceTypeFieldWarning
                                    : ""}
                            </Typography>
                            <Grid container spacing={3}>
                                {resourceTypes.map((resourceTypeOption) => (
                                    <Grid key={resourceTypeOption} md={6} item>
                                        <RadioButton
                                            theme={theme}
                                            value={
                                                resourceTypeOption == "Internal"
                                            }
                                            label={resourceTypeOption}
                                            checked={
                                                lineGroupLocal.is_internal ==
                                                (resourceTypeOption ==
                                                    "Internal")
                                            }
                                            onClick={handleResourceTypeChange}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                        {!isRunning && (
                            <Grid md={6} item height={"max-content"}>
                                <Typography
                                    sx={{ paddintTop: 2, paddingBottom: 1 }}
                                >
                                    Markup{" "}
                                    {popupType
                                        ? popupTypeMapping[popupType]
                                              .markupFieldWarning
                                        : ""}
                                </Typography>
                                {lineGroupLocal.is_internal ? (
                                    <TextField
                                        value="n/a"
                                        size="small"
                                        fullWidth
                                        sx={{ textAlign: "right" }}
                                        inputProps={{
                                            style: { textAlign: "right" },
                                        }}
                                        disabled
                                    />
                                ) : (
                                    <TextField
                                        type="number"
                                        value={
                                            (
                                                lineGroupLocal.markup * 100
                                            ).toFixed() == 0
                                                ? ""
                                                : (
                                                      lineGroupLocal.markup *
                                                      100
                                                  ).toFixed()
                                        }
                                        onChange={handleInput("markup")}
                                        onWheel={(event) => event.target.blur()}
                                        size="small"
                                        fullWidth
                                        sx={{ textAlign: "right" }}
                                        InputProps={{
                                            endAdornment: (
                                                <span
                                                    style={{
                                                        fontSize: 16,
                                                        color: "grey",
                                                        marginLeft: 8,
                                                    }}
                                                >
                                                    %
                                                </span>
                                            ),
                                        }}
                                        inputProps={{
                                            style: { textAlign: "right" },
                                        }}
                                    />
                                )}
                            </Grid>
                        )}
                        {!isRunning && (
                            <CustomTextFieldGridItem
                                lineGroup={lineGroupLocal}
                                handleInput={handleInput}
                                displayName="Markup Amount"
                                fieldName="markup_amount"
                                isPercentage={false}
                                fieldWarning={
                                    popupType
                                        ? popupTypeMapping[popupType]
                                              .markupAmountFieldWarning
                                        : ""
                                }
                            />
                        )}
                        <CustomTextFieldGridItem
                            lineGroup={lineGroupLocal}
                            handleInput={handleInput}
                            displayName="Budget Buffer"
                            fieldName="budget_buffer"
                            isPercentage={true}
                            fieldWarning={
                                popupType
                                    ? popupTypeMapping[popupType]
                                          .budgetBufferFieldWarning
                                    : ""
                            }
                        />
                        <CustomTextFieldGridItem
                            lineGroup={lineGroupLocal}
                            handleInput={handleInput}
                            displayName="Budget Buffer Amount"
                            fieldName="budget_buffer_amount"
                            isPercentage={false}
                            fieldWarning={
                                popupType
                                    ? popupTypeMapping[popupType]
                                          .budgetBufferAmountFieldWarning
                                    : ""
                            }
                        />
                    </Grid>
                </div>
                <Grid container justifyContent={"right"} spacing={3}>
                    <Grid item md={3}>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={handleCancel}
                        >
                            Cancel
                        </Button>
                    </Grid>
                    <Grid item md={3}>
                        <Button
                            fullWidth
                            variant="contained"
                            sx={{ color: "black" }}
                            onClick={
                                popupType
                                    ? popupTypeMapping[popupType].buttonCallback
                                    : null
                            }
                        >
                            {popupType
                                ? popupTypeMapping[popupType].buttonText
                                : ""}
                        </Button>
                    </Grid>
                </Grid>

                <Modal
                    open={warningOpen}
                    onClose={() => {
                        setWarningOpen(false);
                    }}
                >
                    <CustomBox
                        sx={{ background: theme.palette.background.default }}
                    >
                        <TargetsWarning>
                            <Grid container spacing={1}>
                                <Grid item>
                                    <DangerCircle />
                                </Grid>
                                <Grid item>
                                    <Subtitle>Attention</Subtitle>
                                    <Typography
                                        sx={{ paddintTop: 2, paddingBottom: 1 }}
                                    >
                                        These changes will be applied to all
                                        existing line items in this{" "}
                                        {lineGroupType}. <br /> Please ensure
                                        that your modifications are intended to
                                        overwrite existing values before
                                        proceeding
                                    </Typography>
                                </Grid>
                                <Grid
                                    container
                                    justifyContent={"right"}
                                    spacing={3}
                                >
                                    <Grid item md={3}>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            onClick={() => {
                                                setWarningOpen(false);
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    </Grid>
                                    <Grid item md={3}>
                                        {groupUpdating ? (
                                            <CircularProgress />
                                        ) : (
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                onClick={updateLineGroup}
                                            >
                                                Yes! Override
                                            </Button>
                                        )}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </TargetsWarning>
                    </CustomBox>
                </Modal>
            </CustomBox>
        </Modal>
    );
}

const CustomTextFieldGridItem = ({
    lineGroup,
    handleInput,
    displayName,
    fieldName,
    isPercentage,
    fieldWarning,
}) => {
    const val = isPercentage
        ? lineGroup[fieldName] * 100
        : lineGroup[fieldName] * 1;
    return (
        <Grid md={6} item height={"max-content"}>
            <Typography sx={{ paddintTop: 2, paddingBottom: 1 }}>
                {displayName} {fieldWarning || ""}
            </Typography>
            <TextField
                type="number"
                value={val.toFixed() == 0 ? "" : val.toFixed()}
                onChange={handleInput(fieldName)}
                onWheel={(event) => event.target.blur()}
                size="small"
                fullWidth
                sx={{ textAlign: "right" }}
                InputProps={{
                    endAdornment: isPercentage ? (
                        <span style={{ fontSize: 16, color: "grey" }}>%</span>
                    ) : null,
                }}
                inputProps={{ style: { textAlign: "right" } }}
            />
        </Grid>
    );
};
